import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TikTokPreview from '@components/Shared/TikTok/Preview';
import TokensIcon from '@assets/images/klipshow_token.png';
import AddToLibrary from './Actions/AddToLibrary';
import AddToQueue from './Actions/AddToQueue';
import StreamerLibraryActions from './Actions/StreamerLibrary';
import { useKlipPlayCounts } from '@shared/hooks/useKlipPlayCounts';

const formatCount = (value) => {
    const num = Number(value) || 0;
    const abs = Math.abs(num);

    const units = [
        { value: 1e6, suffix: 'M' },
        { value: 1e3, suffix: 'k' },
    ];

    for (const unit of units) {
        if (abs >= unit.value) {
            const scaled = abs / unit.value;
            const rounded = scaled >= 10 ? Math.round(scaled) : Math.round(scaled * 10) / 10;
            return `${num < 0 ? '-' : ''}${rounded}${unit.suffix}`;
        }
    }

    return `${num}`;
};

const KlipCard = ({ klip, streamer_klip, type, action_cable_url }) => {
    const [loaded, setLoaded] = useState(false);
    const [streamerKlip, setStreamerKlip] = useState(streamer_klip);
    const [showActions, setShowActions] = useState(false);
    const [tikTokTitle, setTikTokTitle] = useState(false);
    const playCountResourceType = streamerKlip ? 'streamer_klip' : 'klip'
    const playCountResourceId = streamerKlip?.id || klip?.id
    const { playCount, subscribed } = useKlipPlayCounts(action_cable_url, {
        resourceType: playCountResourceType,
        resourceId: playCountResourceId,
        playCount: streamerKlip?.play_count ?? klip?.play_count,
    })
    const previewReady = () => {
        setLoaded(true);
    };

    const displayActions = () => {
        switch (type) {
            case 'library':
                return (
                    <>
                        {showActions ? (
                            <AddToLibrary klip={klip} setShowActions={setShowActions} />
                        ) : (
                            <button
                                type="button"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer w-full"
                                onClick={() => setShowActions(true)}
                            >
                                <i data-feather="plus" className="w-4 h-4"></i>
                                Add to My Klips
                            </button>
                        )}
                    </>
                )
            case 'streamer_library':
                return (
                    <StreamerLibraryActions streamerKlip={streamerKlip} setStreamerKlip={setStreamerKlip} />
                )
            case 'streamer_cue':
                return (
                    <AddToQueue klip={klip} />
                )
            case 'viewer_cue':
                return (
                    <AddToQueue klip={klip} fromViewer={true} />
                )
        }
    };
    const renderStatus = () => {
        const statuses = [{
            "status": "pending",
            "label": "Pending",
            "color": "bg-amber-500"
        }, {
            "status": "approved",
            "label": "Active",
            "color": "bg-green-500"
        }, {
            "status": "rejected",
            "label": "Rejected",
            "color": "bg-red-500"
        }, {
            "status": "disabled",
            "label": "Disabled",
            "color": "bg-gray-500"
        }]

        const status = statuses.find(status => status.status === streamerKlip.status)

        return (
            <div className="absolute -top-3 -right-3 z-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color} text-white`}>
                    {status.label}
                </span>
            </div>
        )
    }

    return (
        <>
            <TikTokPreview
                id={klip.platform_id}
                previewReady={previewReady}
                showTikTokDescription={true}
                muted={true}
                sourceUrl={klip.metadata?.source_url}
                setTitleFromTikTok={(title) => setTikTokTitle(title)}
            />
            {type === "streamer_library" && (
                renderStatus()
            )}
            <div className="flex justify-between items-center mb-3 text-sm text-gray-300 mt-4">
                <div className="flex-1 gap-1">
                    <div className="flex-1 flex items-center gap-1">
                        <div className="w-[16px] h-[16px]">
                            <i data-feather="eye" className="w-full h-full"></i>
                        </div>
                        <span
                            data-play-count-type={playCountResourceType}
                            data-play-count-id={playCountResourceId}
                            data-play-count-subscribed={subscribed}
                        >
                            {formatCount(playCount || 0)}
                        </span>
                        {streamer_klip && (
                            <div className="flex items-center gap-1">
                                <img src={TokensIcon} alt="Tokens" className="w-6 h-6" />
                                <span className="font-bold">
                                    {streamer_klip.price || 1}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div id="klip-title">
                    {streamer_klip && (
                        <div className="flex-2 flex justify-end">
                            {streamer_klip.title}
                        </div>
                    )}
                    {(!streamer_klip && tikTokTitle) && (
                        <div className="flex-2 flex justify-end">
                            {tikTokTitle.length > 50 ? `${tikTokTitle.substring(0, 50)}...` : tikTokTitle}
                        </div>
                    )}
                </div>
            </div>

            {loaded && (
                displayActions()
            )}
        </>
    );
};

KlipCard.propTypes = {
    klip: PropTypes.object.isRequired,
    action_cable_url: PropTypes.string,
};

export default KlipCard;